import React, { useState, useEffect, useCallback } from 'react';
import { Row, Col, Button, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faClock,
  faEnvelope,
  faHeadset
} from '@fortawesome/free-solid-svg-icons';
import useToken from 'hooks/useToken';
import Avatar from 'components/common/Avatar';

// Formato Title Case para nombres
const formatTitleCase = str => {
  if (!str) return '';
  return str
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

// Tarjeta individual de asesor
const AgentCard = ({ agent, token }) => {
  const [photoUrl, setPhotoUrl] = useState(() => {
    return localStorage.getItem(`perfil_${agent.idUsuario}`) || null;
  });

  useEffect(() => {
    let isMounted = true;
    if (photoUrl) return;

    const fetchAgentPhoto = async () => {
      try {
        const resp = await fetch(`/api/perfil/obtenerImagenPerfil/?idUsuario=${agent.idUsuario}`, {
          headers: {
            Authorization: 'Bearer ' + token,
            'Content-Type': 'text/plain'
          }
        });
        if (resp.ok) {
          const base64Data = await resp.text();
          if (base64Data && base64Data.length > 50 && isMounted) {
            setPhotoUrl(base64Data);
            localStorage.setItem(`perfil_${agent.idUsuario}`, base64Data);
          }
        }
      } catch {
        // Fallback a iniciales
      }
    };

    fetchAgentPhoto();
    return () => {
      isMounted = false;
    };
  }, [agent.idUsuario, token, photoUrl]);

  const fullName = formatTitleCase(agent.vNombreCompleto);
  const roleName = agent.vNombreTipoUsuario || 'Asesor de Soporte';
  const isActive = agent.bIsActive;

  return (
    <div className="se-card-panel text-center position-relative h-100 d-flex flex-column justify-content-between p-3">
      <div>
        {/* Status Badge */}
        <span
          className="position-absolute top-0 end-0 m-3 d-flex align-items-center gap-1"
          style={{
            fontSize: '0.72rem',
            fontWeight: 600,
            color: isActive ? '#10b981' : '#94a3b8'
          }}
          title={isActive ? 'Usuario activo en el sistema' : 'Usuario inactivo'}
        >
          <span
            className="rounded-circle"
            style={{
              width: '8px',
              height: '8px',
              backgroundColor: isActive ? '#10b981' : '#94a3b8'
            }}
          ></span>
          {isActive ? 'Activo' : 'Inactivo'}
        </span>

        {/* Avatar / Photo */}
        <div className="d-inline-flex justify-content-center align-items-center mt-2 mb-3">
          {photoUrl ? (
            <img
              src={photoUrl}
              alt={fullName}
              className="rounded-circle shadow-sm"
              style={{ width: '72px', height: '72px', objectFit: 'cover' }}
              onError={() => setPhotoUrl(null)}
            />
          ) : (
            <Avatar
              name={fullName || 'Asesor'}
              size="3xl"
              rounded="circle"
              className="shadow-sm"
            />
          )}
        </div>

        {/* Name & Role */}
        <h5
          className="fw-bold text-900 mb-1 text-truncate"
          title={fullName}
          style={{ fontSize: '1.02rem', letterSpacing: '-0.01em' }}
        >
          {fullName}
        </h5>

        <p className="text-muted mb-2 text-truncate" style={{ fontSize: '0.8rem' }} title={roleName}>
          {roleName}
        </p>

        {/* Work Email */}
        {agent.vEmailTrabajo && (
          <p
            className="text-muted text-truncate mb-3"
            title={agent.vEmailTrabajo}
            style={{ fontSize: '0.76rem' }}
          >
            <FontAwesomeIcon icon={faEnvelope} className="me-1 opacity-50" />
            {agent.vEmailTrabajo}
          </p>
        )}

        {/* Metric Stats */}
        <div className="d-flex justify-content-around py-2 bg-light rounded-3 mb-3" style={{ fontSize: '0.78rem' }}>
          <div>
            <div className="fw-bold text-900 fs-0">{agent.iChatsActivos || 0}</div>
            <div className="text-muted" style={{ fontSize: '0.72rem' }}>Chats Activos</div>
          </div>
          <div className="border-start"></div>
          <div>
            <div className="fw-bold text-900 fs-0">{agent.iMaxCurrentTicket || 0}</div>
            <div className="text-muted" style={{ fontSize: '0.72rem' }} title="Pico máximo de tickets abiertos concurrentes hoy">Máx. Hoy</div>
          </div>
          <div className="border-start"></div>
          <div>
            <div className="fw-bold text-900 fs-0">{agent.iResueltosHoy || 0}</div>
            <div className="text-muted" style={{ fontSize: '0.72rem' }}>Resueltos Hoy</div>
          </div>
        </div>
      </div>

      {/* Response and Resolution Time Footer */}
      <div className="d-flex align-items-center justify-content-center gap-2 text-muted border-top pt-2 mt-auto" style={{ fontSize: '0.74rem' }}>
        <FontAwesomeIcon icon={faClock} className="opacity-75" />
        <span>
          Resp: <strong>{agent.iTiempoRespuestaPromedioMinutos || 0} min</strong>
        </span>
        <span>•</span>
        <span>
          Cierre: <strong>{agent.iTiempoResolucionPromedioMinutos || 0} min</strong>
        </span>
      </div>
    </div>
  );
};

const AgentsView = () => {
  const { token } = useToken();
  const [agents, setAgents] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Cargar lista de asesores con métricas desde la API
  const fetchAgents = useCallback(async () => {
    if (!token) return;
    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch('/api/SupportEngine/GetAgentsTeamList', {
        headers: {
          Accept: 'application/json',
          Authorization: 'Bearer ' + token
        }
      });

      if (!response.ok) {
        throw new Error(`Error en el servidor (${response.status})`);
      }

      const result = await response.json();
      const teamData = result.data || (Array.isArray(result) ? result : []);
      setAgents(Array.isArray(teamData) ? teamData : []);
    } catch (err) {
      console.error('Error cargando equipo de soporte:', err);
      setError(err.message || 'No se pudo cargar la información del equipo.');
    } finally {
      setIsLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchAgents();
  }, [fetchAgents]);

  return (
    <div>
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-3">
        <div>
          <h2 className="fw-bold text-900 mb-1" style={{ fontSize: '1.65rem', letterSpacing: '-0.02em' }}>
            Equipo de Soporte & Asesores
          </h2>
          <p className="text-muted mb-0" style={{ fontSize: '0.9rem' }}>
            Métricas individuales, disponibilidad y carga de trabajo en tiempo real.
          </p>
        </div>

      </div>

      {/* Content Area */}
      {isLoading ? (
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" style={{ width: '2.5rem', height: '2.5rem' }} />
          <p className="text-muted mt-3 mb-0" style={{ fontSize: '0.88rem' }}>
            Consultando asesores y métricas del equipo...
          </p>
        </div>
      ) : error ? (
        <div className="se-card-panel text-center py-5">
          <h5 className="fw-bold text-danger mb-2">Error al cargar asesores</h5>
          <p className="text-muted mb-3" style={{ fontSize: '0.86rem' }}>{error}</p>
          <Button variant="primary" size="sm" onClick={() => fetchAgents()}>
            Reintentar
          </Button>
        </div>
      ) : agents.length === 0 ? (
        <div className="se-card-panel text-center py-5">
          <FontAwesomeIcon icon={faHeadset} style={{ fontSize: '2.5rem', color: '#cbd5e1' }} className="mb-3" />
          <h5 className="fw-bold text-900 mb-1">No se encontraron asesores</h5>
          <p className="text-muted mb-0" style={{ fontSize: '0.85rem' }}>
            No hay asesores registrados en la base de datos.
          </p>
        </div>
      ) : (
        <Row className="g-3">
          {agents.map(agent => (
            <Col key={agent.idUsuario} xs={12} sm={6} xl={3}>
              <AgentCard agent={agent} token={token} />
            </Col>
          ))}
        </Row>
      )}
    </div>
  );
};

export default AgentsView;
